import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts, ProTip } from '../../components/LessonUI';

const JSSpreadRest = () => {
    const spreadBasicsCode = `// 1. Array Spread (Expansion)
const base = [1, 2];
const combined = [0, ...base, 3]; // [0, 1, 2, 3]

// 2. Object Spread (Expansion)
const defaultSettings = { theme: 'light', zoom: 100 };
const userSettings = { ...defaultSettings, theme: 'dark' }; 

// 3. Merging (Later keys overwrite earlier ones)
const final = { ...defaultSettings, ...userSettings };`;

    const shallowCopyHazardCode = `// --- The Shallow Copy Hazard ---
const original = {
    id: 1,
    meta: { roles: ['admin'] } 
};

// Spread creates a shallow clone
const clone = { ...original };

//  WARNING: Nested objects share references!
clone.meta.roles.push('editor');

console.log(original.meta.roles); // ['admin', 'editor']
// The original was accidentally modified!`;

    const immutabilityPatternCode = `// --- Immutable Update Pattern (React/Redux) ---
const state = {
    user: { id: 1, name: 'Alice' },
    posts: [
        { id: 101, title: 'Hello World' },
        { id: 102, title: 'ES6 Spread' }
    ]
};

// Updating a single post without mutating the state
const updatedState = {
    ...state,
    posts: state.posts.map(post => 
        post.id === 101 ? { ...post, title: 'Updated Title' } : post
    )
};`;

    const restParameterCode = `// --- Rest Parameter (Collection) ---
// Essential for dynamic argument handling

const logger = (level, ...messages) => {
    // Arrow functions DON'T have an 'arguments' object.
    // Rest is the modern solution.
    console.log(\`[\${level}]:\`, ...messages);
};

logger('DEBUG', 'Init process', { step: 1 }, 'Success');`;

    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Functional Patterns</div>
                <h1>Spread & Rest Mastery</h1>
                <p className="hero-lead">
                    While they share the same syntax (<code>...</code>), the Spread and
                    Rest operators serve opposite purposes. One <strong>expands</strong>
                    data into a context, while the other <strong>collects</strong>
                    data into a structure.
                </p>
            </div>

            <div className="content-section">
                <h2>The Spread Operator (Expansion)</h2>
                <p>
                    Spread allows you to "explode" an array or object into a position
                    where multiple elements or properties are expected. It is the
                    modern, idiomatic replacement for <code>Array.prototype.concat</code>
                    and <code>Object.assign</code>.
                </p>
                <CodeBlock language="javascript" code={spreadBasicsCode} />
            </div>

            <div className="content-section">
                <h2>The Shallow Copy Hazard</h2>
                <p>
                    It is critical to understand that spread creates a <strong>shallow copy</strong>.
                    While top-level primitives are copied by value, any nested
                    objects or arrays are copied by <strong>reference</strong>.
                    Modifying a nested object in a spread clone will leak mutations
                    back to the original source.
                </p>
                <CodeBlock language="javascript" code={shallowCopyHazardCode} />
            </div>

            <div className="content-section">
                <h2>The Immutability Pattern</h2>
                <p>
                    In frameworks like React and Redux, state must never be mutated.
                    Spread is the primary tool for creating new state objects that
                    incorporate changes while preserving the rest of the tree.
                </p>
                <CodeBlock language="javascript" code={immutabilityPatternCode} />
            </div>

            <div className="content-section">
                <h2>The Rest Parameter (Collection)</h2>
                <p>
                    Rest parameters allow a function to accept an indefinite number
                    of arguments as an array. Unlike the legacy <code>arguments</code>
                    object, rest parameters are <strong>true arrays</strong> and
                    work perfectly within arrow functions.
                </p>
                <CodeBlock language="javascript" code={restParameterCode} />
                <ProTip><strong> Performance Tip:</strong> Spread is highly optimized
                    in V8, but spreading massive arrays (e.g., millions of elements)
                    into a function call can lead to "Maximum call stack size exceeded"
                    errors. Use <code>for</code> loops for extreme data sets.</ProTip>
            </div>

            <KeyFacts><ul>
                    <li><strong>Expansion:</strong> Use spread to clone and merge objects/arrays cleanly.</li>
                    <li><strong>Collection:</strong> Use rest parameters <code>(...args)</code> instead of the legacy <code>arguments</code> object.</li>
                    <li> <strong>Mutation:</strong> Remember that nested objects are still references in a spread clone.</li>
                    <li><strong>Ordering:</strong> In object spread, the order matterslater keys override earlier ones.</li>
                    <li><strong>State:</strong> Always use spread for top-level state updates in functional programming patterns.</li>
                </ul></KeyFacts>

        </div>
    );
};

export default JSSpreadRest;


